/**
 * Single source of truth for indicator colors + glyphs.
 *
 * Anywhere an "indicator" is rendered (competences/values/personality, the
 * org-profile variants, or the global match) — buttons, pills, bars, badges,
 * tooltips — pulls its class strings from here. Editing a colour in this
 * file flows everywhere, with no per-component drift.
 */
import { BriefcaseIcon, CrownIcon, NetworkIcon, UserIcon, type LucideIcon } from 'lucide-react'
import { CompetencesGlyph, GlobalGlyph, PersonalityGlyph, ValuesGlyph } from '@/components/custom/indicator-glyphs'

export type IndicatorType = 'competences' | 'values' | 'personality'

export type IndicatorKind =
  | 'competences'
  | 'values'
  | 'personality'
  | 'basic'
  | 'professional'
  | 'transversal'
  | 'directives'
  | 'global'

export interface IndicatorPalette {
  /** Glyph used by IndicatorIcon and CompletionDots. */
  icon: LucideIcon
  /** Foreground colour used inside the pill, on text, on bar fills, etc. */
  activeText: string
  /** 10%-tinted bg used by the IndicatorIcon pill, button active state. */
  activeBg: string
  /** 20% border used by buttons in their active state. */
  activeBorder: string
  /** 50% border for the "selected standard profile" affordance. */
  activeBorderStrong: string
  /** Solid bg for badges. */
  badgeBg: string
  /** Foreground for badge text on the solid bg. */
  badgeText: string
  /** Hover variants for inactive buttons. */
  hoverBg: string
  hoverText: string
  hoverBorder: string
  /** Two-stop linear gradient (e.g. used by completion dot fills). */
  gradient: string
  /** Ring colour used to outline the pill on focus / standard state. */
  ringColor: string
  /** Solid fill used by progress / score bars. */
  barFill: string
  /** 15% tinted track used by progress / score bars. */
  barTrack: string
}

export const indicatorPalette: Record<IndicatorKind, IndicatorPalette> = {
  competences: {
    icon: CompetencesGlyph,
    activeText: 'text-[#e78a08]',
    activeBg: 'bg-[#e78a08]/10',
    activeBorder: 'border-[#e78a08]/15',
    activeBorderStrong: 'border-[#e78a08]/40',
    badgeBg: 'bg-[#e78a08]',
    badgeText: 'text-white',
    hoverBg: 'hover:bg-[#e78a08]/10',
    hoverText: 'hover:text-[#e78a08]',
    hoverBorder: 'hover:border-[#e78a08]/15',
    gradient: 'from-[#e78a08] to-orange-500',
    ringColor: 'ring-[#e78a08]',
    barFill: 'bg-[#e78a08]',
    barTrack: 'bg-[#e78a08]/15',
  },
  values: {
    icon: ValuesGlyph,
    activeText: 'text-rose-600',
    activeBg: 'bg-rose-600/10',
    activeBorder: 'border-rose-600/15',
    activeBorderStrong: 'border-rose-600/40',
    badgeBg: 'bg-rose-600',
    badgeText: 'text-white',
    hoverBg: 'hover:bg-rose-600/10',
    hoverText: 'hover:text-rose-600',
    hoverBorder: 'hover:border-rose-600/15',
    gradient: 'from-rose-500 to-pink-500',
    ringColor: 'ring-rose-600',
    barFill: 'bg-rose-600',
    barTrack: 'bg-rose-600/15',
  },
  personality: {
    icon: PersonalityGlyph,
    activeText: 'text-violet-500 dark:text-violet-400',
    activeBg: 'bg-violet-500/10 dark:bg-violet-400/10',
    activeBorder: 'border-violet-500/15 dark:border-violet-400/15',
    activeBorderStrong: 'border-violet-500/40 dark:border-violet-400/40',
    badgeBg: 'bg-violet-500 dark:bg-violet-400',
    badgeText: 'text-white',
    hoverBg: 'hover:bg-violet-500/10 dark:hover:bg-violet-400/10',
    hoverText: 'hover:text-violet-500 dark:hover:text-violet-400',
    hoverBorder: 'hover:border-violet-500/15 dark:hover:border-violet-400/15',
    gradient: 'from-violet-500 to-violet-500',
    ringColor: 'ring-violet-500',
    barFill: 'bg-violet-500',
    barTrack: 'bg-violet-500/15',
  },
  // Org-type filters (used in the orgs panel + match scoring): icons match the
  // org-type buttons in the toolbar.
  basic: {
    icon: UserIcon,
    activeText: 'text-[#e78a08]',
    activeBg: 'bg-[#e78a08]/10',
    activeBorder: 'border-[#e78a08]/15',
    activeBorderStrong: 'border-[#e78a08]/40',
    badgeBg: 'bg-[#e78a08]',
    badgeText: 'text-white',
    hoverBg: 'hover:bg-[#e78a08]/10',
    hoverText: 'hover:text-[#e78a08]',
    hoverBorder: 'hover:border-[#e78a08]/15',
    gradient: 'from-amber-400 to-[#e78a08]',
    ringColor: 'ring-[#e78a08]',
    barFill: 'bg-[#e78a08]',
    barTrack: 'bg-[#e78a08]/15',
  },
  professional: {
    icon: BriefcaseIcon,
    activeText: 'text-rose-600',
    activeBg: 'bg-rose-600/10',
    activeBorder: 'border-rose-600/15',
    activeBorderStrong: 'border-rose-600/40',
    badgeBg: 'bg-rose-600',
    badgeText: 'text-white',
    hoverBg: 'hover:bg-rose-600/10',
    hoverText: 'hover:text-rose-600',
    hoverBorder: 'hover:border-rose-600/15',
    gradient: 'from-rose-500 to-rose-600',
    ringColor: 'ring-rose-600',
    barFill: 'bg-rose-600',
    barTrack: 'bg-rose-600/15',
  },
  transversal: {
    icon: NetworkIcon,
    activeText: 'text-violet-600',
    activeBg: 'bg-violet-600/10',
    activeBorder: 'border-violet-600/15',
    activeBorderStrong: 'border-violet-600/40',
    badgeBg: 'bg-violet-600',
    badgeText: 'text-white',
    hoverBg: 'hover:bg-violet-600/10',
    hoverText: 'hover:text-violet-600',
    hoverBorder: 'hover:border-violet-600/15',
    gradient: 'from-violet-500 to-violet-600',
    ringColor: 'ring-violet-600',
    barFill: 'bg-violet-600',
    barTrack: 'bg-violet-600/15',
  },
  directives: {
    icon: CrownIcon,
    activeText: 'text-emerald-600',
    activeBg: 'bg-emerald-600/10',
    activeBorder: 'border-emerald-600/15',
    activeBorderStrong: 'border-emerald-600/40',
    badgeBg: 'bg-emerald-600',
    badgeText: 'text-white',
    hoverBg: 'hover:bg-emerald-600/10',
    hoverText: 'hover:text-emerald-600',
    hoverBorder: 'hover:border-emerald-600/15',
    gradient: 'from-emerald-500 to-emerald-600',
    ringColor: 'ring-emerald-600',
    barFill: 'bg-emerald-600',
    barTrack: 'bg-emerald-600/15',
  },
  // Global match summary. Deepened to teal-600 (vs the brand `primary`, a 50%-lightness teal
  // that reads washed-out at small sizes): greens/teals appear brighter than reds/violets at
  // equal lightness, so a darker shade is needed to sit balanced next to the dim colours.
  global: {
    icon: GlobalGlyph,
    activeText: 'text-teal-600',
    activeBg: 'bg-teal-600/10',
    activeBorder: 'border-teal-600/20',
    activeBorderStrong: 'border-teal-600/40',
    badgeBg: 'bg-teal-600',
    badgeText: 'text-white',
    hoverBg: 'hover:bg-teal-600/10',
    hoverText: 'hover:text-teal-600',
    hoverBorder: 'hover:border-teal-600/20',
    gradient: 'from-teal-500 to-teal-600',
    ringColor: 'ring-teal-600',
    barFill: 'bg-teal-600',
    barTrack: 'bg-teal-600/15',
  },
}

// Match the market filter buttons (FilterBadge): emerald active state, dashed
// muted inactive state with an emerald hover.
export const sourceButtonColors = {
  active: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
  inactive: 'border-dashed bg-transparent text-muted-foreground hover:bg-emerald-500/10 hover:text-emerald-500',
} as const
